import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Chip,
  Box,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import {
  object as objectPropType,
  func as funcPropType,
  bool,
} from "prop-types";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: "600px",
    maxWidth: "800px",
  },
  dialogContent: {
    maxHeight: "600px",
    overflowY: "auto",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#3A6ED4",
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  educationItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  experienceItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  label: {
    fontWeight: 500,
    color: "#666",
  },
  value: {
    marginTop: theme.spacing(0.5),
  },
  aboutText: {
    marginTop: theme.spacing(0.5),
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.6,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  emptyState: {
    color: "#999",
    fontStyle: "italic",
  },
}));

const ResumePreviewModal = ({
  open,
  handleClose,
  parsedData,
  onApply,
  isLoading,
}) => {
  const classes = useStyles();

  const handleApplyData = () => {
    onApply(parsedData);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
    >
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          Review Extracted Resume Data
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Review the information extracted from your resume. You can apply this
          data to your profile.
        </Typography>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              Parsing your resume...
            </Typography>
          </div>
        ) : (
          <>
            {/* Basic Information */}
            <div className={classes.section}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Basic Information
              </Typography>
              {parsedData?.first_name || parsedData?.last_name ? (
                <Box>
                  <Typography className={classes.label}>Name:</Typography>
                  <Typography className={classes.value}>
                    {parsedData?.first_name} {parsedData?.last_name}
                  </Typography>
                </Box>
              ) : (
                <Typography className={classes.emptyState}>
                  No name found
                </Typography>
              )}

              {parsedData?.email && (
                <Box mt={1}>
                  <Typography className={classes.label}>Email:</Typography>
                  <Typography className={classes.value}>
                    {parsedData.email}
                  </Typography>
                </Box>
              )}

              {parsedData?.phone && (
                <Box mt={1}>
                  <Typography className={classes.label}>Phone:</Typography>
                  <Typography className={classes.value}>
                    {parsedData.phone}
                  </Typography>
                </Box>
              )}
            </div>

            <Divider />

            {/* Professional Summary */}
            {parsedData?.about && (
              <>
                <div className={classes.section}>
                  <Typography
                    variant="subtitle1"
                    className={classes.sectionTitle}
                  >
                    Professional Summary
                  </Typography>
                  <Typography variant="body2" className={classes.aboutText}>
                    {parsedData.about}
                  </Typography>
                </div>
                <Divider />
              </>
            )}

            {/* Skills */}
            {parsedData?.skills && parsedData.skills.length > 0 && (
              <>
                <div className={classes.section}>
                  <Typography
                    variant="subtitle1"
                    className={classes.sectionTitle}
                  >
                    Skills ({parsedData.skills.length})
                  </Typography>
                  <Box>
                    {parsedData.skills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        className={classes.chip}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </div>
                <Divider />
              </>
            )}

            {/* Education */}
            {parsedData?.education && parsedData.education.length > 0 && (
              <>
                <div className={classes.section}>
                  <Typography
                    variant="subtitle1"
                    className={classes.sectionTitle}
                  >
                    Education ({parsedData.education.length})
                  </Typography>
                  {parsedData.education.map((edu, index) => (
                    <div key={index} className={classes.educationItem}>
                      {edu.degree && (
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {edu.degree}
                        </Typography>
                      )}
                      {edu.institution && (
                        <Typography variant="body2" color="textSecondary">
                          {edu.institution}
                        </Typography>
                      )}
                      {edu.year && (
                        <Typography variant="caption" color="textSecondary">
                          {edu.year}
                        </Typography>
                      )}
                    </div>
                  ))}
                </div>
                <Divider />
              </>
            )}

            {/* Experience */}
            {parsedData?.experience && parsedData.experience.length > 0 && (
              <div className={classes.section}>
                <Typography
                  variant="subtitle1"
                  className={classes.sectionTitle}
                >
                  Work Experience ({parsedData.experience.length})
                </Typography>
                {parsedData.experience.map((exp, index) => (
                  <div key={index} className={classes.experienceItem}>
                    {exp.title && (
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {exp.title}
                      </Typography>
                    )}
                    {exp.company && (
                      <Typography variant="body2" color="textSecondary">
                        {exp.company}
                      </Typography>
                    )}
                    {exp.dates && (
                      <Typography variant="caption" color="textSecondary">
                        {exp.dates}
                      </Typography>
                    )}
                    {exp.description && (
                      <Typography variant="body2" style={{ marginTop: 8 }}>
                        {exp.description}
                      </Typography>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Empty state if no data */}
            {!parsedData?.first_name &&
              !parsedData?.last_name &&
              !parsedData?.email &&
              !parsedData?.phone &&
              !parsedData?.about &&
              (!parsedData?.skills || parsedData.skills.length === 0) &&
              (!parsedData?.education || parsedData.education.length === 0) &&
              (!parsedData?.experience ||
                parsedData.experience.length === 0) && (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" className={classes.emptyState}>
                    No data could be extracted from the resume.
                    <br />
                    Please check the file format and try again.
                  </Typography>
                </Box>
              )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleApplyData}
          color="primary"
          variant="contained"
          disabled={isLoading}
        >
          Apply to Profile
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ResumePreviewModal.propTypes = {
  open: bool.isRequired,
  handleClose: funcPropType.isRequired,
  parsedData: objectPropType,
  onApply: funcPropType.isRequired,
  isLoading: bool,
};

ResumePreviewModal.defaultProps = {
  parsedData: null,
  isLoading: false,
};

export default ResumePreviewModal;
